import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, User, Eye, Loader2, Search, Sparkles } from "lucide-react";
import { useCategories, useArticles } from "@/hooks/useKBData";
import { formatDistanceToNow } from "date-fns";


export function KBHomePage() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const categoryFilter = searchParams.get("category");

  // Fetch data
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  const { data: articlesData, isLoading: articlesLoading } = useArticles();

  const categories = categoriesData?.categories || [];
  const articles = articlesData?.articles || [];

  // Filter articles by category if specified
  const filteredArticles = categoryFilter
    ? articles.filter(article => {
        const category = categories.find(cat => cat.id === article.category_id);
        return category?.slug === categoryFilter;
      })
    : articles;

  // Sort articles by views for better display order
  const displayArticles = filteredArticles
    .sort((a, b) => (b.views || 0) - (a.views || 0));

  // Get selected category info for display
  const selectedCategory = categoryFilter
    ? categories.find(cat => cat.slug === categoryFilter)
    : null;

  return (
    <div>
      {/* Hero Section - Only on "All Articles" */}
      {!selectedCategory && (
        <div className="mb-10 p-8 rounded-3xl bg-gradient-to-br from-primary/5 via-primary/10 to-transparent border border-primary/10">
          <div className="flex items-center gap-2 text-primary mb-3">
            <Sparkles className="h-5 w-5" />
            <span className="text-sm font-semibold">RealtyHaus Knowledge Base</span>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mb-3">
            How can we help you today?
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mb-6">
            Find answers, learn best practices, and get the most out of RealtyHaus.
          </p>

          {/* Mobile Search */}
          <button
            onClick={() => setSearchOpen(true)}
            className="md:hidden flex items-center gap-3 px-4 py-3 bg-background border border-border rounded-xl text-sm text-muted-foreground w-full"
          >
            <Search className="h-4 w-4" />
            <span>Search articles...</span>
          </button>
        </div>
      )}

      {/* Category Header - When filtered */}
      {selectedCategory && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">{selectedCategory.icon}</span>
            <h1 className="text-2xl font-bold tracking-tight">{selectedCategory.name}</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {filteredArticles.length} article{filteredArticles.length !== 1 ? "s" : ""}
          </p>
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/kb">← Back to All Articles</Link>
          </Button>
        </div>
      )}

      {/* Category Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {selectedCategory ? selectedCategory.name : 'All Articles'}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {filteredArticles.length} article{filteredArticles.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Article Grid */}
      <div>
        {articlesLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : displayArticles.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No articles found
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {displayArticles.map((article) => {
              // Find the category for this article
              const category = categories.find(cat => cat.id === article.category_id);

              return (
                <article
                  key={article.id}
                  className="group p-5 rounded-2xl border border-border bg-card hover:border-primary/20 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                >
                  <Link to={`/app/kb/${article.slug}`} className="block">
                    {/* Category Badge */}
                    <div className="mb-3">
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground">
                        <span className="text-xs">{category?.icon}</span>
                        {category?.name || "Uncategorized"}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-bold text-base leading-snug mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {article.title}
                    </h3>

                    {/* Excerpt */}
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-2">
                      {article.excerpt || "No preview available"}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="font-medium">{article.author_name}</span>
                      <span className="text-border">•</span>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatDistanceToNow(new Date(article.updated_at), { addSuffix: true })}</span>
                      </div>
                      <span className="text-border">•</span>
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        <span>{article.views || 0}</span>
                      </div>
                    </div>
                  </Link>
                </article>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {displayArticles.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="font-semibold text-lg mb-2">No articles found</h3>
            <p className="text-muted-foreground text-sm">
              {selectedCategory
                ? `There are no articles in ${selectedCategory.name} category yet.`
                : 'There are no articles available.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
}